'use client';

import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface ProgressToastContentProps {
  status: string;
  progress?: number;
}

function ProgressToastContent({ status, progress }: ProgressToastContentProps) {
  const pct = typeof progress === 'number' ? Math.max(0, Math.min(100, Math.round(progress))) : undefined;
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span className="text-sm">{status}</span>
        {pct !== undefined && <span className="ml-auto text-sm tabular-nums">{pct}%</span>}
      </div>
      {pct !== undefined && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all duration-200"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <p className="text-xs opacity-75">Stay on the page until the process is complete.</p>
    </div>
  );
}

interface ShowProgressToastArgs {
  title: string;
  status: string;
  progress?: number;
}

export interface ProgressToastHandle {
  update: (next: { title?: string; status?: string; progress?: number }) => void;
  dismiss: () => void;
}

export function showProgressToast(args: ShowProgressToastArgs): ProgressToastHandle {
  let current = { title: args.title, status: args.status, progress: args.progress };

  const t = toast({
    title: current.title,
    description: <ProgressToastContent status={current.status} progress={current.progress} />,
    duration: Infinity,
    dismissible: false,
  });

  return {
    update: (next) => {
      current = {
        title: next.title ?? current.title,
        status: next.status ?? current.status,
        progress: next.progress ?? current.progress,
      };
      t.update({
        title: current.title,
        description: <ProgressToastContent status={current.status} progress={current.progress} />,
      });
    },
    dismiss: t.dismiss,
  };
}
